import { useEffect, useState } from 'react';
import { X, Megaphone } from 'lucide-react';

const DISMISS_KEY = 'beta_banner_dismissed_v1';

/**
 * BetaBanner — soft-launch announcement bar.
 *
 * Receives the public beta-mode state from LandingPage (single fetch shared
 * across components). Renders only when enabled. Dismissible per browser
 * via localStorage, keyed on the message text so admin copy updates
 * automatically re-show the banner.
 */
export function BetaBanner({ betaMode }) {
  const enabled = !!betaMode?.enabled;
  const message = betaMode?.message || '';
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Reset dismissed state whenever the message changes.
    setDismissed(localStorage.getItem(DISMISS_KEY) === message);
  }, [message]);

  if (!enabled || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, message);
    setDismissed(true);
  };

  return (
    <div
      className="relative w-full bg-[#007a33] text-white text-sm"
      role="status"
      aria-live="polite"
      data-testid="beta-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 pr-12 flex items-center justify-center gap-2 text-center">
        <Megaphone className="w-4 h-4 flex-shrink-0 opacity-90" />
        <span className="font-medium" data-testid="beta-banner-message">{message}</span>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss beta banner"
        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md hover:bg-white/15 transition-colors"
        data-testid="beta-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
